import { FC, useEffect, useState } from "react";
import {
    IonAlert,
    IonButton,
    IonCard,
    IonCardContent,
    IonContent,
    IonIcon,
    IonInput,
    IonItem,
    IonLabel,
    IonLoading,
    IonPage,
    IonText,
} from "@ionic/react";
import { HeaderComponent } from "../../../../components";
import {
    Controller,
    FieldValues,
    FormProvider,
    useForm,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
    linkSoccerRefManagerCode,
    syncSoccerRefManagerMatches,
    disconnectSoccerRefManagerSubscription,
} from "../../../../services/integrationService";
import { routes } from "../../../../route/constants";
import { refreshSubscriptions } from "../../../../services/subscriptionService";
import { IntegrationPartner, SubscriptionItems } from "refsix-js-models";
import { useSubscription } from "../../../../components/hooks";
import { syncOutline } from "ionicons/icons";
import { trackEvent } from "../../../../services/analytics/analyticsService";
import * as Sentry from "@sentry/react";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import refsixSquareLogo from "../../../../assets/images/icon/refsix-square-icon.png";
import soccerRefManagerSquareLogo from "../../../../assets/images/icon/soccerrefmanager-square-icon.png";

const SoccerRefManagerIntegration: FC = () => {
    const { t } = useTranslation();
    const developerToolsEnabled = useSelector(
        (state: RootState) => state.developerTools.enabled
    );
    const [inputsDisabled, setInputsDisabled] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(false);
    const subscriptions = useSubscription();
    const [isSubscriptionValid, setIsSubscriptionValid] =
        useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [showSuccessAlert, setShowSuccessAlert] = useState<boolean>(false);

    const formMethods = useForm<FieldValues>({
        mode: "all",
        defaultValues: {
            refereeCode: "",
        },
    });

    const { watch } = formMethods;
    const refereeCode = watch("refereeCode");

    useEffect(() => {
        const soccerRefManagerSubscription =
            subscriptions?.subscriptions.find(
                (subscription: SubscriptionItems) =>
                    subscription.provider ===
                    IntegrationPartner.soccerRefManager
            );
        if (
            soccerRefManagerSubscription &&
            new Date(soccerRefManagerSubscription.validUntil) > new Date()
        ) {
            setIsSubscriptionValid(true);
            formMethods.setValue(
                "refereeCode",
                soccerRefManagerSubscription.rawData?.refereeCode
            );
            setInputsDisabled(true);
        } else {
            setIsSubscriptionValid(false);
            setInputsDisabled(false);
            formMethods.setValue("refereeCode", "");
        }
    }, [formMethods, subscriptions]);

    const linkSoccerRefManager = async () => {
        setErrorMessage(null);
        setLoading(true);
        if (!refereeCode) {
            setErrorMessage(t("integrations.soccerRefManager.invalidCode"));
            setLoading(false);
            return;
        }
        try {
            await linkSoccerRefManagerCode(refereeCode);
            trackEvent("AddIntegrationSuccess", {
                partner: IntegrationPartner.soccerRefManager,
            });
            // The account is now linked; a refresh/sync failure past this point
            // must not be reported as a link failure, so keep both in the inner
            // try (only a genuine link rejection reaches the outer catch).
            try {
                await refreshSubscriptions();
                await syncSoccerRefManagerMatches();
                setLoading(false);
                setShowSuccessAlert(true);
                setInputsDisabled(true);
                setIsSubscriptionValid(true);
            } catch (error: any) {
                console.warn(
                    "Error syncing with SoccerRefManager:",
                    error
                );
                setErrorMessage(
                    t("integrations.soccerRefManager.syncFailed")
                );
                setLoading(false);
            }
        } catch (error: any) {
            console.warn("Error while linking SoccerRefManager:", error);
            trackEvent("AddIntegrationFailed", {
                partner: IntegrationPartner.soccerRefManager,
            });
            // request (axios) rejects on any non-2xx, so a bad code surfaces
            // here rather than as a resolved non-200 response. The backend
            // returns 400/403 for an invalid referee code.
            const status = error?.response?.status;
            if (status === 400 || status === 403) {
                setErrorMessage(
                    `${t("integrations.soccerRefManager.invalidCode")} (${status})`
                );
            } else {
                setErrorMessage(t("integrations.soccerRefManager.linkFailed"));
            }
            setInputsDisabled(false);
            setIsSubscriptionValid(false);
            setLoading(false);
        }
    };

    const syncWithSoccerRefManager = async () => {
        setLoading(true);
        try {
            await syncSoccerRefManagerMatches();
        } catch (error) {
            console.warn("SoccerRefManager sync failed", error);
            setErrorMessage(t("integrations.soccerRefManager.syncFailed"));
        }
        setLoading(false);
    };

    const disconnectSoccerRefManager = async () => {
        setErrorMessage(null);
        setLoading(true);
        try {
            const response = await disconnectSoccerRefManagerSubscription();
            if (response.status === 200) {
                trackEvent("RemoveIntegrationSuccess", {
                    partner: IntegrationPartner.soccerRefManager,
                });
                setLoading(false);
                refreshSubscriptions().then(() => {
                    setIsSubscriptionValid(false);
                });
            } else {
                trackEvent("RemoveIntegrationFailed", {
                    partner: IntegrationPartner.soccerRefManager,
                });
                setLoading(false);
                setErrorMessage(
                    t("integrations.soccerRefManager.disconnectFailed")
                );
            }
        } catch (error) {
            console.warn(
                "Error while disconnecting SoccerRefManager:",
                error
            );
            Sentry.captureException(error);
            setLoading(false);
            setErrorMessage(
                t("integrations.soccerRefManager.disconnectFailed")
            );
        }
    };

    return (
        <IonPage>
            <HeaderComponent
                showBackButton
                defaultHref={routes.refsixFixtures}
                title="SoccerRefManager"
            />
            <IonContent>
                <IonLoading isOpen={loading} />
                <IonAlert
                    isOpen={showSuccessAlert}
                    onDidDismiss={() => setShowSuccessAlert(false)}
                    message={t("integrations.successMessage")}
                    buttons={[t("help.OnboardingVideo.controls.ok")]}
                />
                <div
                    style={{
                        justifyContent: "center",
                        alignItems: "center",
                        display: "flex",
                        paddingTop: "36px",
                        paddingBottom: "21px",
                    }}
                >
                    <img
                        style={{ width: "64px" }}
                        src={refsixSquareLogo}
                        alt={"refsix logo"}
                    />
                    <IonText
                        style={{
                            margin: "0 20px",
                            fontSize: "30px",
                            fontWeight: "700",
                        }}
                    >
                        +
                    </IonText>
                    <img
                        style={{
                            width: "64px",
                            borderRadius: "12px",
                            filter: "brightness(0) invert(1)",
                        }}
                        src={soccerRefManagerSquareLogo}
                        alt={"SoccerRefManager logo"}
                    />
                </div>
                <IonCard style={{ marginBottom: "16px" }}>
                    <IonCardContent>
                        <IonText
                            style={{
                                fontWeight: "bold",
                                fontSize: "24px",
                                color: "white",
                            }}
                        >
                            {t(
                                "integrations.soccerRefManager.integrationDescriptionHeading"
                            )}
                        </IonText>
                        <p style={{ color: "white", marginBottom: "16px" }}>
                            <IonText>
                                {t(
                                    "integrations.soccerRefManager.integrationDescription"
                                )}
                            </IonText>
                        </p>
                        <IonText
                            style={{
                                fontWeight: "bold",
                                fontSize: "24px",
                                color: "white",
                            }}
                        >
                            {t(
                                "integrations.soccerRefManager.integrationInstructionsHeading"
                            )}
                        </IonText>
                        <p style={{ color: "white", marginBottom: "16px" }}>
                            <IonText>
                                {t(
                                    "integrations.soccerRefManager.integrationInstructions"
                                )}
                            </IonText>
                        </p>
                    </IonCardContent>
                </IonCard>
                <IonCard color="">
                    <FormProvider {...formMethods}>
                        <form>
                            <IonItem disabled={inputsDisabled} lines="none">
                                <IonLabel>
                                    {t(
                                        "integrations.soccerRefManager.refereeCode"
                                    )}
                                </IonLabel>
                                <Controller
                                    rules={{ required: true }}
                                    name="refereeCode"
                                    render={({
                                        field: { onBlur, onChange, ...rest },
                                    }) => (
                                        <IonInput
                                            onIonChange={onChange}
                                            data-testid="input-soccerrefmanager-code"
                                            {...rest}
                                        />
                                    )}
                                />
                            </IonItem>
                        </form>
                    </FormProvider>
                </IonCard>
                <IonCard>
                    {!isSubscriptionValid && (
                        <IonButton
                            disabled={inputsDisabled}
                            expand="block"
                            onClick={linkSoccerRefManager}
                            data-testid="button-integration-soccerrefmanager-connect"
                        >
                            {t("general.connect")}
                        </IonButton>
                    )}
                    {isSubscriptionValid && (
                        <>
                            <IonButton
                                expand="block"
                                data-testid="button-integration-soccerrefmanager-disconnect"
                                onClick={disconnectSoccerRefManager}
                            >
                                {t("general.disconnect")}
                            </IonButton>
                            {developerToolsEnabled && (
                                <IonButton
                                    expand="block"
                                    onClick={syncWithSoccerRefManager}
                                    data-testid="button-integration-soccerrefmanager-sync"
                                >
                                    <IonIcon
                                        slot="start"
                                        icon={syncOutline}
                                        style={{ marginRight: "8px" }}
                                    />
                                    Sync
                                </IonButton>
                            )}
                        </>
                    )}
                </IonCard>

                {errorMessage && (
                    <IonCard color="danger">
                        <IonItem>
                            <IonText color="danger">
                                <p>{errorMessage}</p>
                            </IonText>
                        </IonItem>
                    </IonCard>
                )}
            </IonContent>
        </IonPage>
    );
};

export default SoccerRefManagerIntegration;
